<script module lang="ts">
  import type { MemberCard } from './member';
  // Shared across all hover triggers so a card is fetched once per member, keyed by
  // the host's own scope — v1 scopes a member to a community in the PATH, so the
  // community is part of what identifies a card. The component does not know what
  // the scope IS; it asks the host for a string and compares strings.
  const cardCache = new Map<string, MemberCard>();
</script>

<script lang="ts">
  /**
   * The member hovercard — ONE implementation, two apps, one route.
   *
   * It used to be two: this one on `GET /v1/communities/{id}/members/{member_id}`, and
   * a near-copy in the extension on the frozen `GET /admin/analytics/member-card`,
   * because a device token was once 403 on every `/v1` community route. That table is
   * gone — a device token now carries the user's own role — so both apps call this one
   * route, `fetchMemberCard` is the only reader, and there is no adapter left on either
   * side to drift.
   *
   * The `memberId`-only signature is kept deliberately — it is what makes the card
   * droppable at fourteen call sites across nine files without threading a community
   * through any of them. The community reaches it via `memberCardScope()` rather than
   * a store read, which is the same trick one layer down and the reason this works
   * outside the web app at all.
   */
  import type { Snippet } from 'svelte';
  import { ui } from './host';
  import { fetchMemberCard, journeySegments, timeAgo } from './member';
  import Heatmap from './Heatmap.svelte';
  import Avatar from './Avatar.svelte';
  import PlanCell from './PlanCell.svelte';
  import RenewalCell from './RenewalCell.svelte';
  import RoleJourney from './RoleJourney.svelte';
  import { stageMeta } from './stages';
  import { quadMeta } from './quadrant';
  import { memberStatusMeta } from './member';

  let {
    memberId,
    children,
    class: cls = '',
    style: styleStr = '',
  }: { memberId: number; children: Snippet; class?: string; style?: string } = $props();

  let open = $state(false);
  let card = $state<MemberCard | null>(null);
  let pos = $state({ x: 0, y: 0, above: false });
  let showTimer: ReturnType<typeof setTimeout> | undefined;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;

  const CARD_W = 320;
  const CARD_H = 340;

  function place(el: HTMLElement): void {
    const r = el.getBoundingClientRect();
    let x = Math.max(8, Math.min(r.left, window.innerWidth - CARD_W - 8));
    const above = r.bottom + 6 + CARD_H > window.innerHeight && r.top - 6 > CARD_H;
    pos = { x, y: above ? r.top - 6 : r.bottom + 6, above };
  }

  function onEnter(e: MouseEvent): void {
    clearTimeout(hideTimer);
    const el = e.currentTarget as HTMLElement;
    showTimer = setTimeout(async () => {
      place(el);
      open = true;
      const scope = ui().memberCardScope();
      if (scope == null) return;
      const key = `${scope}:${memberId}`;
      const cached = cardCache.get(key);
      if (cached) {
        card = cached;
        return;
      }
      try {
        const c = await fetchMemberCard(Number(scope), memberId);
        cardCache.set(key, c);
        card = c;
      } catch {
        // Swallowed on purpose, including the 403 for a community this credential does
        // not hold. A hovercard that cannot load is a tooltip that quietly does not
        // appear — never an error surfaced to someone who was only moving their mouse.
      }
    }, 350);
  }
  function onLeave(): void {
    clearTimeout(showTimer);
    hideTimer = setTimeout(() => (open = false), 160);
  }
  function cardEnter(): void {
    clearTimeout(hideTimer);
  }
  function cardLeave(): void {
    hideTimer = setTimeout(() => (open = false), 120);
  }
  // The whole card is a link to the member's profile. Keep it a real <a href> so
  // middle-/cmd-click opens the profile in a new tab; intercept the plain click for
  // SPA navigation (and dismiss the card) while letting modified clicks fall through.
  function goProfile(e: MouseEvent, id: number): void {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    open = false;
    ui().openMemberProfile(id);
  }
</script>

<span class="inline-flex cursor-pointer {cls}" style={styleStr} onmouseenter={onEnter} onmouseleave={onLeave} role="presentation">
  {@render children()}
</span>

{#if open}
  <div
    class="fixed z-50 w-80 overflow-hidden rounded-box border hair bg-base-100 shadow-2xl shadow-black/50"
    style="left:{pos.x}px; top:{pos.y}px; {pos.above ? 'transform:translateY(-100%)' : ''}"
    onmouseenter={cardEnter}
    onmouseleave={cardLeave}
    onpointerdown={(e) => e.stopPropagation()}
    role="presentation"
  >
    {#if card}
      {@const c = card}
      {@const journey = journeySegments(c.journey)}
      {@const st = c.stats ?? null}
      {@const sm = stageMeta(st?.stage)}
      {@const qm = quadMeta(st?.quadrant)}
      <a
        class="block cursor-pointer p-4 text-left transition-colors hover:bg-base-200/40 focus-visible:bg-base-200/40 focus:outline-none"
        href={ui().memberProfileHref(c.member_id) ?? "#"}
        onclick={(e) => goProfile(e, c.member_id)}
      >
        <!-- The entire card links to the member's profile (no separate link) -->
        <!-- Identity: name · stage, role-journey bar, last active (same as the queue row) -->
        <div class="flex items-center gap-3">
          <Avatar src={c.picture_profile} name={c.name} size={44} memberId={c.member_id} />
          <div class="min-w-0 flex-1">
            <!-- The card's headline is the member's name; the role, plan and activity
                 below it are what the card is FOR and stay sharp. -->
            <span class="redact block truncate font-display text-base font-bold tracking-tight">{c.name}</span>
            {#if journey.length}
              <div class="mt-1.5">
                <RoleJourney {journey} dense />
              </div>
            {/if}
            {#if st?.last_active}
              <!-- v1 emits an instant here, exactly as the legacy card did; `timeAgo`
                   goes through `parseTs`, so a tz-less string can't be read as local. -->
              <div class="mt-1 truncate font-mono text-[0.64rem] text-base-content/60">last active {timeAgo(st.last_active, Date.now())}</div>
            {/if}
          </div>
        </div>

        <!-- Engagement + role as two stats. A member who has left has no momentum to
             report (see stages.ts) — the tile keeps its place so the pair stays
             aligned, and the Role tile beside it is the one that says "Departed". -->
        <div class="mt-3 grid grid-cols-2 gap-1.5 text-center">
          <div class="rounded-field bg-base-200/60 py-1.5">
            {#if sm}
              <div class="truncate font-display text-sm font-bold" style="color: {sm.color}" title={sm.blurb}>{sm.label}</div>
            {:else}
              <div class="truncate font-display text-sm font-bold text-base-content/40" title="No longer a member — momentum doesn't apply">—</div>
            {/if}
            <div class="eyebrow text-[0.55rem]">Engagement</div>
          </div>
          <div class="rounded-field bg-base-200/60 py-1.5">
            <div class="truncate font-display text-sm font-bold" style="color: {qm.color}" title={qm.tip}>{qm.label}</div>
            <div class="eyebrow text-[0.55rem]">Role</div>
          </div>
        </div>

        <!-- MEMBERSHIP — only where we can actually see it. `status === null` means we
             hold no staff role in this community and cannot read the members page, so
             every billing field is null; rendering the block anyway would print "Free"
             and "—" as if they were facts. `Membership.of` nulls the whole block for
             exactly this reason, so the null IS the signal. -->
        {#if c.membership?.status}
          {@const mb = c.membership}
          {@const sm2 = memberStatusMeta(mb.status)}
          <div class="mt-3">
            <div class="eyebrow mb-1.5 text-[0.6rem]">Membership</div>
            <div class="grid grid-cols-2 gap-1.5">
              <!-- Money and renewal through the SAME two components the members table
                   uses, so a plan cannot read one way in a table and another in a
                   tooltip. Both are left-aligned here — the card is a stack of blocks,
                   not a column of figures to compare down. -->
              <div class="rounded-field bg-base-200/60 px-2.5 py-1.5">
                <PlanCell plan={mb} align="left" />
              </div>
              <div class="rounded-field bg-base-200/60 px-2.5 py-1.5">
                <RenewalCell row={mb} align="left" />
              </div>
            </div>
            {#if sm2}
              <div class="mt-1.5 flex items-center gap-1.5 font-mono text-[0.62rem] text-base-content/60">
                <span class="h-1.5 w-1.5 shrink-0 rounded-full" style:background={sm2.color}></span>
                <span>{sm2.label}</span>
                {#if c.joined_at}
                  <span class="ml-auto truncate">joined {timeAgo(c.joined_at, Date.now())}</span>
                {/if}
              </div>
            {/if}
          </div>
        {/if}

        <div class="mt-3">
          <div class="eyebrow mb-1.5 text-[0.6rem]">Last 12 months</div>
          <!-- `activity=dense` — 371 contiguous ascending days, the shape <Heatmap>
               requires. Never hand it `weekly`. -->
          <Heatmap days={c.activity?.days ?? []} max={c.activity?.max ?? 0} months={false} showLabels={false} gap={1} />
        </div>
      </a>
    {:else}
      <div class="flex h-32 items-center justify-center">
        <span class="loading loading-spinner loading-md text-primary/60"></span>
      </div>
    {/if}
  </div>
{/if}
